import { Box, Link, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { mapDispatchToProps, mapStateToProps } from './VerifyPhone.container';

import { FormikPhoneNumber } from '/b2b/common/components/FormControls/PhoneNumber';
import { FormikTextField } from '/b2b/common/components/FormControls/TextField';
import FormikValidationForm from '/b2b/common/components/organisms/FormikValidationForm';
import Locale from '/b2b/common/components/Locale';
import PropTypes from 'prop-types';
import ReselectMFA from '../components/ReselectMFA';
import { connect } from 'react-redux';
import { verifyPhoneSchema } from './VerifyPhone.schema';

// eslint-disable-next-line no-use-before-define
export default connect(mapStateToProps, mapDispatchToProps)(VerifyPhone);

/**
 * This component handles the phone number verification process. This IS NOT (and cannot be) used to verify an SMS MFA code.
 *
 * SMS MFA codes are verified through the [SignInConfirm]({@link ./../../SignIn/SignInConfirm/SignInConfirm.component.jsx}) component
 */
export function VerifyPhone(props) {
    const { isPhoneVerified, phone, verify, resendPhoneVerificationCode, codeDeliveryDetails } =
        props;
    const [resent, setResent] = useState(null);
    /** @type {React.RefObject<import('formik').FormikBag<{ phone: string, code: string }, { phone: string[], code: string[] }>>} */
    const formikRef = useRef(null);

    const handleResend = useCallback(() => {
        formikRef.current.validateForm().then(formikErrors => {
            formikRef.current.setErrors(formikErrors);
            if (!formikErrors.phone) {
                return (
                    !1 &&
                    resendPhoneVerificationCode()
                        .then(() => setResent(true))
                        .catch(e => setResent(e.message))
                );
            }
        });
    }, [resendPhoneVerificationCode]);

    useEffect(() => {
        // This is a precaution so that we don't resend the verification code if the phone is already verified, because it could lead to a SMS denial of service.
        !isPhoneVerified && resendPhoneVerificationCode();
    }, [isPhoneVerified]);

    return (
        <Box
            display="flex"
            flexDirection="column"
            height="100%"
            alignItems="stretch"
            justifyContent="center"
            sx={{ mx: 'auto', px: 2, maxWidth: 700, width: '100%' }}
        >
            <FormikValidationForm
                formikRef={formikRef}
                enableReinitialize
                validateOnMount
                initialValues={{ phone }}
                validationSchema={verifyPhoneSchema}
                onSubmit={verify}
                title="auth.signIn.confirm.title"
                subtitle="auth.phone.confirmationCode.description"
                submitText="auth.signIn.continue"
                translationOptions={codeDeliveryDetails ?? {}}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    width="100%"
                    flexGrow={1}
                    gap={0.5}
                    alignItems="center"
                    mt={4}
                    mb={1}
                    sx={theme => (theme.breakpoints.down('lg') ? { justifyContent: 'center' } : {})}
                >
                    <FormikPhoneNumber
                        name="phone"
                        label="common.fields.phone.label"
                        autoComplete="phone"
                        fullWidth
                        required
                        disabled
                        mb={1}
                    />
                    <FormikTextField
                        name="code"
                        label="common.fields.code.label"
                        placeholder="auth.amplify.mfa.sms.placeholder"
                        fullWidth
                        required
                        mb={1}
                    />
                </Box>
            </FormikValidationForm>
            <ReselectMFA mfaType="SMS" />
            <Box mt={5}>
                <Typography variant="body1">
                    <Locale path="auth.phone.confirmationCode.didNotGetCode" />{' '}
                    <Link
                        fontWeight="bold"
                        onClick={handleResend}
                        tabIndex={0}
                        sx={{ cursor: 'pointer' }}
                    >
                        <Locale path="password.resend" />
                    </Link>
                </Typography>
                <Typography color={resent === true ? 'success' : 'error'} variant="body2">
                    {resent === true ? (
                        <Locale path="auth.phone.confirmationCode.resent" />
                    ) : (
                        resent
                    )}
                </Typography>
            </Box>
        </Box>
    );
}

VerifyPhone.propTypes = {
    className: PropTypes.string,
    isPhoneVerified: PropTypes.bool,
    phone: PropTypes.string,
    username: PropTypes.string,
    hasPhone: PropTypes.bool,
    resent: PropTypes.bool,
    codeDeliveryDetails: PropTypes.object,
    verify: PropTypes.func.isRequired,
    resendPhoneVerificationCode: PropTypes.func.isRequired,
};
